import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, CreditCard, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";

const PAY_LABEL = { knet: "كي نت", visa: "فيزا (سجل سابق)", mastercard: "ماستركارد (سجل سابق)", cash_store: "نقداً في المحل" };
const PAY_LABEL_EN = { knet: "KNET", visa: "Visa (legacy)", mastercard: "Mastercard (legacy)", cash_store: "Cash in store" };
const PAID = ["MEASUREMENTS_REVIEW", "IN_PRODUCTION", "READY", "OUT_FOR_DELIVERY", "SHIPPED", "DELIVERED", "PAID"];

export default function Pay() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const { lang, tr } = useLang();

  const refresh = () => api.get(`/orders/${id}/public`).then((r) => setOrder(r.data)).catch(() => nav("/"));
  useEffect(() => { refresh(); }, [id]);
  if (!order) return <div className="py-24 text-center text-text-muted">…</div>;

  const isCash = order.payment_method === "cash_store";
  const isPaid = PAID.includes(order.status);
  const isCashAwaiting = order.status === "AWAITING_CASH_PAYMENT";
  const needsPay = ["AWAITING_PAYMENT", "PAYMENT_FAILED", "PAYMENT_CANCELLED"].includes(order.status) && !isCash;

  const sim = async (outcome) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${id}/simulate-payment`, { outcome });
      setOrder(data);
      if (outcome === "success") toast.success("تم الدفع بنجاح (وضع تجريبي)");
      else if (outcome === "fail") toast.error("فشل الدفع — يمكنك إعادة المحاولة");
      else toast("تم إلغاء الدفع");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const Summary = () => (
    <div className="bg-surface border border-border p-5 text-sm text-start space-y-1.5 my-6">
      <div className="flex justify-between"><span className="text-text-muted">{tr("رقم الطلب", "Order no.")}</span><span dir="ltr" data-testid="order-ref">{order.order_ref}</span></div>
      {order.invoice_ref && <div className="flex justify-between"><span className="text-text-muted">{tr("رقم الفاتورة", "Invoice no.")}</span><span dir="ltr" data-testid="invoice-ref">{order.invoice_ref}</span></div>}
      <div className="flex justify-between"><span className="text-text-muted">{tr("طريقة الدفع", "Payment method")}</span><span>{(lang === "en" ? PAY_LABEL_EN : PAY_LABEL)[order.payment_method] || order.payment_method}</span></div>
      <div className="flex justify-between"><span className="text-text-muted">{tr("حالة الدفع", "Payment status")}</span><span className={isPaid ? "text-green-700" : "text-amber-700"} data-testid="order-payment-status">{isPaid ? tr("مدفوع", "Paid") : tr("بانتظار الدفع", "Awaiting payment")}</span></div>
      <div className="flex justify-between"><span className="text-text-muted">{tr("الحالة", "Status")}</span><span className="text-accent" data-testid="order-status">{order.status_label}</span></div>
      <div className="flex justify-between border-t border-border pt-1.5 mt-1.5"><span>{tr("مجموع القطع", "Items subtotal")}</span><span dir="ltr" data-testid="pay-subtotal">{formatKWD(order.subtotal, lang)}</span></div>
      <div className="flex justify-between"><span>{tr("التوصيل / الشحن", "Delivery / shipping")}</span><span dir="ltr" data-testid="pay-delivery">{formatKWD(order.delivery_fee, lang)}</span></div>
      <div className="flex justify-between font-medium"><span>{tr("الإجمالي", "Total")}</span><span dir="ltr" data-testid="pay-total">{formatKWD(order.total, lang)}</span></div>
    </div>
  );

  if (needsPay) {
    return (
      <div className="max-w-lg mx-auto px-6 py-12 md:py-16 fade-in text-center">
        <CreditCard className="w-11 h-11 mx-auto text-primary mb-4" strokeWidth={1.2} />
        <h1 className="font-display text-3xl mb-1">{tr("صفحة الدفع", "Payment")}</h1>
        <div className="inline-flex items-center gap-1.5 bg-surface border border-border text-[11px] text-text-muted px-3 py-1.5 mt-2" data-testid="test-mode-banner">
          <ShieldAlert className="w-3.5 h-3.5 text-accent" /> {tr("لم يتم الدفع بعد — بوابة الدفع الإلكتروني قيد الربط النهائي. لم يُخصم أي مبلغ.", "Not paid yet — the online payment gateway is being finalised. No amount has been charged.")}
        </div>
        <Summary />
        {order.status === "PAYMENT_FAILED" && <p className="text-sm text-destructive mb-3" data-testid="pay-failed-msg">فشل الدفع في المحاولة السابقة. حاولي مرة أخرى.</p>}
        {order.status === "PAYMENT_CANCELLED" && <p className="text-sm text-destructive mb-3" data-testid="pay-cancelled-msg">تم إلغاء الدفع. يمكنك إعادة المحاولة.</p>}
        <div className="bg-surface border border-border p-4 text-sm text-text-muted leading-relaxed" data-testid="pay-gateway-pending">
          {tr("بوابة الدفع الإلكتروني (كي نت) قيد الربط النهائي. تم حفظ طلبك بالحالة «بانتظار الدفع» ولم يُخصم أي مبلغ. سنُبلغك فور تفعيل الدفع لإتمام العملية.", "The KNET gateway is being finalised. Your order is saved as “Awaiting payment” and nothing has been charged. We'll notify you once payment is enabled.")}
        </div>
        <Link to="/shop" className="inline-block text-xs text-text-muted underline mt-6">{tr("العودة للتسوق", "Back to shop")}</Link>
      </div>
    );
  }

  // Success / cash-awaiting view
  return (
    <div className="max-w-lg mx-auto px-6 py-16 md:py-20 text-center fade-in">
      {isCashAwaiting ? (
        <CheckCircle2 className="w-12 h-12 mx-auto text-accent mb-5" strokeWidth={1.2} />
      ) : isPaid ? (
        <CheckCircle2 className="w-12 h-12 mx-auto text-primary mb-5" strokeWidth={1.2} data-testid="pay-success-icon" />
      ) : (
        <XCircle className="w-12 h-12 mx-auto text-destructive mb-5" strokeWidth={1.2} />
      )}
      <h1 className="font-display text-3xl mb-2" data-testid="order-success-title">
        {isCashAwaiting ? "تم استلام طلبك" : isPaid ? "تم تأكيد الدفع وطلبك قيد التجهيز" : "حالة الطلب"}
      </h1>
      <p className="text-sm text-text-muted mb-1" data-testid="order-payment-message">{order.payment?.message}</p>
      {isPaid && (order.destination?.transit) && (
        <p className="text-sm text-accent mb-1" data-testid="delivery-eta">تم تأكيد طلبك. مدة التوصيل المتوقعة: {order.destination.transit}</p>
      )}
      <Summary />
      {isCashAwaiting && (
        <p className="text-xs text-text-muted mb-6">تُصدر الفاتورة النهائية بعد تأكيد الموظفة استلام المبلغ في المحل.</p>
      )}
      <div className="flex flex-wrap gap-3 justify-center">
        <Link to="/account" className="btn-secondary px-6 py-3 text-sm" data-testid="go-account">طلباتي</Link>
        <Link to="/shop" className="btn-primary px-6 py-3 text-sm" data-testid="go-shop">متابعة التسوق</Link>
      </div>
    </div>
  );
}
